import { PaginationState, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fieldsCredentials } from "./FieldsCredentials";
import { fields } from "./Fields";
import { fieldsConfig } from "./FieldsConfig";
import { ConnectError } from "@connectrpc/connect";
import { TableView } from "@components/common/TableView";
import {
  getAgencies,
  getAvailableConfigs,
  onboardAgency,
} from "@services/agency";
import { Credentials, ListResponse } from "@gen/agency/v1/agency_pb";
import { Spinner } from "@thetreep/ui";
import { CreationForm } from "@components/common/CreationForm";
import { useToaster } from "@hooks/useToaster";
import { useUserStore } from "@stores/userStore";
import { Role } from "@gen/user/v1/user_pb";

export type ConfigList = { value: string; label: string }[];
export type Config = { config: string; alias: string };

export function AgencyManagement() {
  const { addToast } = useToaster();
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [isCreationLoading, setIsCreationLoading] = useState(false);
  const [configsList, setConfigsList] = useState<ConfigList>([]);
  const [credentials, setCredentials] = useState<Credentials>();
  const [stepForm, setStepForm] = useState<"fillCredentials" | "chooseConfig">(
    "fillCredentials",
  );
  const [agencies, setAgencies] = useState<ListResponse>();
  const [isLoadingAgencies, setisLoadingAgencies] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [refreshKey, setRefreshKey] = useState(0);
  async function fetchAgencies(page = 0) {
    setisLoadingAgencies(true);

    try {
      const agencies = await getAgencies(page + 1, pagination.pageSize);

      if (page === 0) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: 0,
        }));
        setRefreshKey((prev) => prev + 1);
      }

      setAgencies(agencies);
    } catch (error) {
      console.error("Error fetching agencies:", error);
    } finally {
      setisLoadingAgencies(false);
    }
  }

  async function handleFetchConfig(credentials: Credentials) {
    try {
      setIsCreationLoading(true);
      const res = await getAvailableConfigs(credentials);
      setCredentials(credentials);
      const configs = res?.configs.map((item) => ({
        value: item.id,
        label: item.providerName,
      }));
      setConfigsList(configs);
      setStepForm("chooseConfig");
    } catch (e: unknown) {
      if (e instanceof ConnectError) {
        addToast("error", e.rawMessage);
      }
    } finally {
      setIsCreationLoading(false);
    }
  }
  async function handleOnboard(configId: string, alias: string) {
    if (!credentials) return;

    try {
      setIsCreationLoading(true);
      await onboardAgency({ credentials, configId, alias });
      setIsCreationOpen(false);
      fetchAgencies(0);
      setStepForm("fillCredentials");
      addToast("success", t("admin.agency.onboardingSuccess"));
    } catch (e: unknown) {
      if (e instanceof ConnectError) {
        addToast("error", e.rawMessage);
      }
    } finally {
      setIsCreationLoading(false);
    }
  }

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchAgencies(0);
  }, [sorting]);

  if (!agencies) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <TableView
        fields={fields(t)}
        isLoadingItems={isLoadingAgencies}
        items={agencies?.agencies}
        key={refreshKey}
        title={t("admin.agency.agencyManagement")}
        pagination={pagination}
        sorting={sorting}
        totalItems={Number(agencies.page?.totalElements)}
        setPagination={(value) => {
          const newPagination =
            typeof value === "function" ? value(pagination) : value;
          setPagination(value);
          fetchAgencies(newPagination.pageIndex);
        }}
        setSorting={setSorting}
        refresh={() => fetchAgencies()}
        topActions={
          user && user.role === Role.ADMIN
            ? [
                {
                  label: t("admin.agency.agencyOnboarding"),
                  icon: "add_line",
                  do: () => setIsCreationOpen(true),
                },
              ]
            : undefined
        }
      />

      {stepForm === "fillCredentials" && (
        <CreationForm
          isLoading={isCreationLoading}
          title={t("admin.agency.agencyOnboarding")}
          fields={fieldsCredentials(t)}
          isOpen={isCreationOpen}
          onClose={() => setIsCreationOpen(false)}
          onSubmit={(value) => {
            handleFetchConfig(value);
          }}
        />
      )}
      {stepForm === "chooseConfig" && (
        <CreationForm
          isLoading={isCreationLoading}
          title={t("admin.agency.agencyOnboarding")}
          fields={fieldsConfig(t, configsList)}
          isOpen={isCreationOpen}
          onClose={() => setIsCreationOpen(false)}
          onSubmit={(value: Config) => {
            const configId = value?.config;
            handleOnboard(configId, value.alias);
          }}
        />
      )}
    </>
  );
}
